import React, { Suspense, lazy, useEffect } from 'react';
import { Routes as RouterRoutes, Route, Navigate, useParams, useNavigate, Outlet } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { FeatureScopeGuard, AnyScopeGuard } from '@shared/components/ScopeGuard';
import { MainLayout } from '../layouts/MainLayout';
import { MinimalLayout } from '../layouts/MinimalLayout';
import { AdminLayout } from '../layouts/AdminLayout';

// Import BreadcrumProviders directly (not lazy - they're small wrapper components)
import { BreadcrumProvider as ProjectBreadcrumProvider } from '@modules/projects/components/BreadcrumProvider';
import { BreadcrumProvider as InventoryBreadcrumProvider } from '@modules/inventory/providers/BreadcrumProvider';
import { BreadcrumProvider as CompanyBreadcrumProvider } from '@modules/company/providers/BreadcrumProvider';
import { BreadcrumProvider as ProductBuilderBreadcrumProvider } from '@modules/product-builder/providers/BreadcrumProvider';
import { BreadcrumProvider as ProductConfigBreadcrumProvider } from '@modules/product-configuration/components/BreadcrumProvider';
import { ProductConfigurationVariablesProvider } from '@modules/product-configuration/providers/ProductConfigurationVariables';

// Loading fallback component
const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="200px"
  >
    <CircularProgress />
  </Box>
);

// Lazy load page components - handle both named and default exports
const ProjectSearch = lazy(() => import('@modules/projects/pages/ProjectSearch').then(m => ({ default: m.ProjectSearch })));
const ProjectView = lazy(() => import('@modules/projects/pages/ProjectView').then(m => ({ default: m.ProjectView })));
const ProjectCalendar = lazy(() => import('@modules/projects/pages/Calendar').then(m => ({ default: m.ProjectCalendar })));
const ProjectTakeoffView = lazy(() => import('@modules/projects/pages/TakeoffView').then(m => ({ default: m.ProjectTakeoffView })));
const ProjectEstimateGroupPage = lazy(() =>
  import('@modules/projects/pages/ProjectEstimateGroupPage').then(m => ({ default: m.ProjectEstimateGroupPage })),
);
const PrintMaterials = lazy(() => import('@modules/projects/pages/PrintMaterials').then(m => ({ default: m.PrintMaterials })));
const DocumentPreview = lazy(() => import('@modules/documents/preview').then(m => ({ default: m.DocumentPreview })));
const AdminDocumentTemplates = lazy(() =>
  import('@modules/documents/admin').then(m => ({ default: m.TemplatesList })),
);
const AdminDocumentTemplateViewer = lazy(() =>
  import('@modules/documents/admin').then(m => ({ default: m.TemplateViewer })),
);
const AdminDocumentSnippets = lazy(() =>
  import('@modules/documents/admin').then(m => ({ default: m.SnippetsPage })),
);
const AdminDocumentSettings = lazy(() =>
  import('@modules/documents/admin').then(m => ({ default: m.DocumentSettingsPage })),
);
const AdminDocumentsHub = lazy(() =>
  import('@modules/documents/admin').then(m => ({ default: m.DocumentsHubPage })),
);
const AdminMyDocumentSignature = lazy(() =>
  import('@modules/documents/admin').then(m => ({ default: m.MyDocumentSignaturePage })),
);
const AdminDocumentTemplateEditor = lazy(() =>
  import('@modules/documents/editor').then(m => ({ default: m.TemplateEditorPage })),
);

// Inventory - Inventories is default export, Inventory is named
const InventoryList = lazy(() => import('@modules/inventory/pages/Inventories/index'));
const InventoryView = lazy(() => import('@modules/inventory/pages/Inventory/Inventory').then(m => ({ default: m.Inventory })));

// Company - Customers is default export, Customer is named
const CompanyCustomers = lazy(() => import('@modules/company/pages/Customers/index'));
const CompanyCustomer = lazy(() => import('@modules/company/pages/Customer/Customer').then(m => ({ default: m.Customer })));

// Profile
const ProfilePage = lazy(() => import('@modules/profile/pages/Profile/Profile'));
const ProfileSetup = lazy(() => import('@modules/profile/pages/Setup/Setup').then(m => ({ default: m.Setup })));
const ProfileUser = lazy(() => import('@modules/profile/pages/User/User').then(m => ({ default: m.User })));

// Admin
const AdminSettingsLayout = lazy(() => import('@modules/company/pages/Settings/SettingsLayout'));
const AdminPricingSettings = lazy(() => import('@modules/company/pages/Settings/PricingSettings'));
const AdminCrewTypes = lazy(() => import('@modules/company/pages/Settings/CrewTypes'));
const AdminEstimateCategories = lazy(() => import('@modules/company/pages/Settings/EstimateCategories'));
const AdminJobTypes = lazy(() => import('@modules/company/pages/Settings/JobTypes'));
const AdminUsers = lazy(() => import('@modules/admin/pages/Users/Users').then(m => ({ default: m.Users })));
const AdminNoteTemplates = lazy(() => import('@modules/admin/pages/NoteTemplates/NoteTemplates').then(m => ({ default: m.NoteTemplates })));

// Global Documents - named export (in index.tsx within folder)
const GlobalDocuments = lazy(() => import('@modules/global-documents/pages/GlobalDocuments/index').then(m => ({ default: m.GlobalDocuments })));

const PowerhousePage = lazy(() => import('@modules/powerhouse/pages/PowerhousePage').then(m => ({ default: m.PowerhousePage })));
const GeneratedDocumentsList = lazy(() =>
  import('@modules/documents/list').then(m => ({ default: m.GeneratedDocumentsList })),
);

// Product Builder
const ProductBuilderSearch = lazy(() => import('@modules/product-builder/pages/ProductSearch').then(m => ({ default: m.ProductSearch })));
const ProductBuilderCategories = lazy(() => import('@modules/product-builder/pages/ProductCategorys/ProductCategorys').then(m => ({ default: m.ProductCategorys })));
const ProductBuilderCategory = lazy(() => import('@modules/product-builder/pages/ProductCategory/ProductCategory').then(m => ({ default: m.ProductCategory })));
const ProductBuilderSubCategory = lazy(() => import('@modules/product-builder/pages/SubCategory/SubCategory').then(m => ({ default: m.SubCategoryPage })));
const ProductBuilderDynamicMaterial = lazy(() => import('@modules/product-builder/pages/DynamicMaterialList/DynamicMaterialList').then(m => ({ default: m.DynamicMaterialList })));

// Product Configuration
const ProductConfigList = lazy(() => import('@modules/product-configuration/pages/ProductConfigurationsList').then(m => ({ default: m.ProductConfigurationsList })));
const ProductConfigView = lazy(() => import('@modules/product-configuration/pages/ProductView').then(m => ({ default: m.ProductView })));
// Plan
const PlanPageSearch = lazy(() => import('@modules/plan/pages/PlanEditor/PlanPageSearch').then(m => ({ default: m.PlanPageSearch })));
const PlanEditor = lazy(() => import('@modules/plan/pages/PlanEditor').then(m => ({ default: m.PlanEditor })));

/** Matches Admin sidebar link (`pages.ts`); owners / root can always open admin URLs. */
const ADMIN_AREA_SCOPES = ['organization:admin', 'organization:owner', 'global:root'] as const;

export const Routes = () => {
  return (
    <RouterRoutes>
      {/* Root redirect to project calendar */}
      <Route path="/" element={<Navigate to="/project/calendar" replace />} />

      {/* Plan routes - minimal layout (no sidebar/navbar/footer) */}
      <Route element={<MinimalLayout />}>
        <Route path="/plan/:planId/:planPageId" element={<Suspense fallback={<LoadingFallback />}><PlanEditor minimum={true} setMinimum={() => {}} /></Suspense>} />
        <Route path="/plan/:planId" element={<Suspense fallback={<LoadingFallback />}><PlanPageSearch minimum={false} setMinimum={() => {}} /></Suspense>} />
      </Route>

      {/* Print view - outside MainLayout to avoid navigation/sidebar */}
      <Route path="/project/:project/takeoff/:takeoffId/print" element={<ProjectBreadcrumProvider><Suspense fallback={<LoadingFallback />}><PrintMaterials /></Suspense></ProjectBreadcrumProvider>} />
      <Route path="/project/:project/takeoff/:takeoffId/document-preview" element={<ProjectBreadcrumProvider><Suspense fallback={<LoadingFallback />}><DocumentPreview /></Suspense></ProjectBreadcrumProvider>} />
      
      {/* Main layout with nested routes */}
      <Route element={<MainLayout />}>
        {/* Projects */}
        <Route path="/project" element={<ProjectBreadcrumProvider><Suspense fallback={<LoadingFallback />}><ProjectSearch /></Suspense></ProjectBreadcrumProvider>} />
        <Route path="/project/search" element={<ProjectBreadcrumProvider><Suspense fallback={<LoadingFallback />}><ProjectSearch /></Suspense></ProjectBreadcrumProvider>} />
        <Route path="/project/calendar" element={<ProjectBreadcrumProvider><Suspense fallback={<LoadingFallback />}><ProjectCalendar /></Suspense></ProjectBreadcrumProvider>} />
        <Route path="/project/:project" element={<ProjectBreadcrumProvider><Suspense fallback={<LoadingFallback />}><ProjectView /></Suspense></ProjectBreadcrumProvider>} />
        <Route
          path="/project/:project/documents"
          element={
            <ProjectBreadcrumProvider>
              <Suspense fallback={<LoadingFallback />}>
                <GeneratedDocumentsList />
              </Suspense>
            </ProjectBreadcrumProvider>
          }
        />
        <Route
          path="/project/:project/estimate-group"
          element={
            <ProjectBreadcrumProvider>
              <Suspense fallback={<LoadingFallback />}>
                <ProjectEstimateGroupPage />
              </Suspense>
            </ProjectBreadcrumProvider>
          }
        />
        <Route path="/project/:project/takeoff/:takeoffId" element={<ProjectBreadcrumProvider><Suspense fallback={<LoadingFallback />}><ProjectTakeoffView /></Suspense></ProjectBreadcrumProvider>} />

        {/* Inventory */}
        <Route path="/inventory" element={<InventoryBreadcrumProvider><Suspense fallback={<LoadingFallback />}><InventoryList /></Suspense></InventoryBreadcrumProvider>} />
        <Route path="/inventory/:inventoryId" element={<InventoryBreadcrumProvider><Suspense fallback={<LoadingFallback />}><InventoryView /></Suspense></InventoryBreadcrumProvider>} />

        {/* Company / Customers */}
        <Route path="/company" element={<CompanyBreadcrumProvider><Suspense fallback={<LoadingFallback />}><CompanyCustomers /></Suspense></CompanyBreadcrumProvider>} />
        <Route path="/company/:customerId" element={<CompanyBreadcrumProvider><Suspense fallback={<LoadingFallback />}><CompanyCustomer /></Suspense></CompanyBreadcrumProvider>} />

        {/* Admin area with nested routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="/admin/profile" replace />} />
          <Route path="profile" element={<Suspense fallback={<LoadingFallback />}><ProfilePage /></Suspense>} />
          <Route path="documents" element={
            <AnyScopeGuard scopes={[...ADMIN_AREA_SCOPES]}>
              <Outlet />
            </AnyScopeGuard>
          }>
            <Route
              index
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <AdminDocumentsHub />
                </Suspense>
              }
            />
            <Route
              path="my-signature"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <AdminMyDocumentSignature />
                </Suspense>
              }
            />
            <Route
              path="settings"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <AdminDocumentSettings />
                </Suspense>
              }
            />
            <Route
              path="templates"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <AdminDocumentTemplates />
                </Suspense>
              }
            />
            <Route
              path="templates/:id"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <AdminDocumentTemplateViewer />
                </Suspense>
              }
            />
            <Route
              path="templates/:id/edit"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <AdminDocumentTemplateEditor />
                </Suspense>
              }
            />
            <Route
              path="snippets"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <AdminDocumentSnippets />
                </Suspense>
              }
            />
          </Route>
          <Route path="settings" element={
            <AnyScopeGuard scopes={[...ADMIN_AREA_SCOPES]}>
              <Suspense fallback={<LoadingFallback />}>
                <AdminSettingsLayout />
              </Suspense>
            </AnyScopeGuard>
          }>
            <Route index element={<Suspense fallback={<LoadingFallback />}><AdminPricingSettings /></Suspense>} />
            <Route path="crew-types" element={<Suspense fallback={<LoadingFallback />}><AdminCrewTypes /></Suspense>} />
            <Route
              path="estimate-categories"
              element={<Suspense fallback={<LoadingFallback />}><AdminEstimateCategories /></Suspense>}
            />
            <Route path="job-types" element={<Suspense fallback={<LoadingFallback />}><AdminJobTypes /></Suspense>} />
          </Route>
          <Route path="users" element={
            <AnyScopeGuard scopes={[...ADMIN_AREA_SCOPES]}>
              <Suspense fallback={<LoadingFallback />}>
                <AdminUsers />
              </Suspense>
            </AnyScopeGuard>
          } />
          <Route path="note-templates" element={
            <AnyScopeGuard scopes={[...ADMIN_AREA_SCOPES]}>
              <Suspense fallback={<LoadingFallback />}>
                <AdminNoteTemplates />
              </Suspense>
            </AnyScopeGuard>
          } />
        </Route>

        {/* Legacy profile redirects for backward compatibility */}
        <Route path="/profile" element={<Navigate to="/admin/profile" replace />} />
        <Route path="/profile/users" element={<Navigate to="/admin/users" replace />} />
        <Route path="/profile/setup" element={<Suspense fallback={<LoadingFallback />}><ProfileSetup /></Suspense>} />
        <Route path="/user/:userId" element={<Suspense fallback={<LoadingFallback />}><ProfileUser /></Suspense>} />

        {/* Global Documents */}
        <Route path="/files" element={<Suspense fallback={<LoadingFallback />}><GlobalDocuments /></Suspense>} />
        <Route path="/globalDocuments" element={<Navigate to="/files" replace />} />

        {/* PowerHouse Chat Bot (paid add-on — Auth0 permission feature:powerhouse_ai_bot) */}
        <Route
          path="/ai"
          element={
            <FeatureScopeGuard scope="feature:powerhouse_ai_bot">
              <Suspense fallback={<LoadingFallback />}>
                <PowerhousePage />
              </Suspense>
            </FeatureScopeGuard>
          }
        />

        {/* Product Builder */}
        <Route path="/productBuilder" element={<ProductBuilderBreadcrumProvider><Suspense fallback={<LoadingFallback />}><ProductBuilderSearch /></Suspense></ProductBuilderBreadcrumProvider>} />
        <Route path="/productBuilder/:product" element={<ProductBuilderBreadcrumProvider><Suspense fallback={<LoadingFallback />}><ProductBuilderCategories /></Suspense></ProductBuilderBreadcrumProvider>} />
        <Route path="/productBuilder/:product/:category" element={<ProductBuilderBreadcrumProvider><Suspense fallback={<LoadingFallback />}><ProductBuilderCategory /></Suspense></ProductBuilderBreadcrumProvider>} />
        <Route path="/productBuilder/:product/M/:dynamicMaterialId" element={<ProductBuilderBreadcrumProvider><Suspense fallback={<LoadingFallback />}><ProductBuilderDynamicMaterial /></Suspense></ProductBuilderBreadcrumProvider>} />
        <Route path="/productBuilder/:product/:category/:subcategory" element={<ProductBuilderBreadcrumProvider><Suspense fallback={<LoadingFallback />}><ProductBuilderSubCategory /></Suspense></ProductBuilderBreadcrumProvider>} />

        {/* Product Configuration */}
        <Route path="/productConfigs" element={<ProductConfigurationVariablesProvider><ProductConfigBreadcrumProvider><Suspense fallback={<LoadingFallback />}><ProductConfigList /></Suspense></ProductConfigBreadcrumProvider></ProductConfigurationVariablesProvider>} />
        <Route path="/productConfigs/:configId" element={<ProductConfigurationVariablesProvider><ProductConfigBreadcrumProvider><Suspense fallback={<LoadingFallback />}><ProductConfigView /></Suspense></ProductConfigBreadcrumProvider></ProductConfigurationVariablesProvider>} />
        <Route path="/productConfigs/:configId/subproduct/:subConfigId" element={<ProductConfigurationVariablesProvider><ProductConfigBreadcrumProvider><Suspense fallback={<LoadingFallback />}><ProductConfigView /></Suspense></ProductConfigBreadcrumProvider></ProductConfigurationVariablesProvider>} />

        {/* Legacy project URL redirect: /:projectId -> /project/:projectId */}
        {/* This handles old URLs like /6424bc9aa07d5f0a8b0d4a48 */}
        {/* Only matches MongoDB ObjectId patterns (24 hex chars) */}
        <Route path="/:projectId" element={<LegacyProjectRedirect />} />
        
        {/* Catch-all 404 redirect to calendar */}
        <Route path="*" element={<Navigate to="/project/calendar" replace />} />
      </Route>
    </RouterRoutes>
  );
};

// Component to handle legacy project URL redirects
const LegacyProjectRedirect = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Check if this looks like a MongoDB ObjectId (24 hex chars)
    if (projectId && /^[a-f0-9]{24}$/i.test(projectId)) {
      navigate(`/project/${projectId}`, { replace: true });
    } else {
      // If it doesn't look like a project ID, redirect to home
      navigate('/', { replace: true });
    }
  }, [projectId, navigate]);
  
  return <LoadingFallback />;
};
